import { Badge, Combobox, DataTable, Inline, Select, Stack, t } from '@kanbios/ui';
import { FULL_HEIGHT } from '../../lib/tables';
import type { maintenance } from '@kanbios/api-client';
import { useSession } from '../../lib/session';

export type ImportOptions = maintenance.paths['/servers/imports/options']['get']['responses']['200']['content']['application/json'];
type ImportError = maintenance.components['schemas']['ImportError'];
export interface AttachmentDraft {
  server: string;
  environment: string;
  team: string;
  criticality: string;
  deployment: string | null;
}
type Field = 'environment' | 'team' | 'deployment' | 'criticality';
interface FieldProps {
  value: AttachmentDraft;
  onChange: (v: AttachmentDraft) => void;
  options: ImportOptions | undefined;
  errors?: ImportError[];
  /** In a table cell: small, named for screen readers by the server. */
  compact?: boolean;
  disabled?: boolean;
}

/** « Aucune » in the application choice: attached later in Serveurs du projet. */
const NO_APPLICATION = '__none__';
export const FIELDS: Field[] = ['environment', 'team', 'deployment', 'criticality'];

/** One field of a server's attachment, for the Rattachement table and the manual entry alike. */
export function AttachmentField({ field, value, onChange, options, errors = [], compact = false, disabled = false }: FieldProps & { field: Field }) {
  const { enums } = useSession();
  const name = t(`import.attachment.${field}`);
  const common = {
    size: compact ? ('sm' as const) : ('md' as const),
    label: compact ? undefined : name,
    ariaLabel: compact ? t('import.attachment.ariaField', { field: name, server: value.server }) : undefined,
    required: !compact && field !== 'deployment',
    error: errors.find(e => e.server === value.server && e.field === field)?.message,
    disabled,
  };
  switch (field) {
    case 'environment':
      return <Select {...common} options={enums.environments} value={value.environment} onChange={v => onChange({ ...value, environment: v ?? value.environment, deployment: null })} />;
    case 'team':
      return <Select {...common} searchable options={(options?.teams ?? []).map(x => ({ value: x, label: x }))} value={value.team || null}
        onChange={v => onChange({ ...value, team: v ?? '' })} placeholder={t('import.attachment.teamPlaceholder')} />;
    case 'deployment': {
      // The applications of the server's environment, in the person's scope (D48: of the same type).
      const type = enums.environments.find(e => e.value === value.environment)?.type;
      const applications = (options?.deployments ?? []).filter(d => d.environmentType === type).map(d => ({ value: d.deployment, label: d.label }));
      return <Combobox {...common} searchable options={[{ value: NO_APPLICATION, label: t('import.attachment.none') }, ...applications]}
        value={value.deployment ?? NO_APPLICATION} onChange={(v: string | null) => onChange({ ...value, deployment: !v || v === NO_APPLICATION ? null : v })} />;
    }
    case 'criticality':
      return <Select {...common} options={enums.criticalities.map(c => ({ value: c.value, label: c.label }))} value={value.criticality}
        onChange={v => onChange({ ...value, criticality: v ?? value.criticality })} />;
  }
}

/**
 * Rattachement (SPEC-007): environment, team, application and criticality of
 * each server, with the rules of SPEC-006 as the backend returns them.
 */
export function AttachmentTable({ rows, onChange, options, errors, warnings, disabled = false }: {
  rows: AttachmentDraft[];
  onChange: (v: AttachmentDraft) => void;
  options: ImportOptions | undefined;
  errors: ImportError[];
  warnings: { server: string; message: string }[];
  disabled?: boolean;
}) {
  const cell = (field: Field) => (r: AttachmentDraft) => <AttachmentField compact field={field} value={r} onChange={onChange} options={options} errors={errors} disabled={disabled} />;
  return (
    <DataTable
      label={t('import.attachment.table')}
      rows={rows}
      rowKey="server"
      toolbar={false}
      // Editable cells: a field's error shows under it, so rows take their height. The server stays on one line.
      rowHeight="auto"
      height={FULL_HEIGHT}
      columns={[
        { key: 'server', label: t('settings.access.columns.server'), mono: true, width: 200, pinned: true },
        { key: 'environment', label: t('import.attachment.environment'), width: 130, editable: true, sortable: false, render: cell('environment') },
        { key: 'team', label: t('import.attachment.team'), width: 150, editable: true, sortable: false, render: cell('team') },
        {
          // The rules of SPEC-006 under the application they concern.
          key: 'deployment', label: t('import.attachment.deployment'), width: 180, grow: true, editable: true, sortable: false,
          render: (r: AttachmentDraft) => {
            const own = [...new Set(warnings.filter(w => w.server === r.server).map(w => w.message))];
            return (
              <Stack gap={4}>
                {cell('deployment')(r)}
                {own.length > 0 && <Inline gap={4} wrap={false}>{own.map(m => <Badge key={m} tone="warn" size="sm">{m}</Badge>)}</Inline>}
              </Stack>
            );
          },
        },
        { key: 'criticality', label: t('import.attachment.criticality'), width: 130, editable: true, sortable: false, render: cell('criticality') },
      ]}
    />
  );
}
